import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { all_routes } from "../../router/all_routes";
import {
  SectionCard,
  FieldLabel,
  ReadOnlyField,
  BottomActionBar,
  WarningBanner,
  useConfirm,
} from "./shared/components";
import { WithdrawalOriginalSections } from "./shared/WithdrawalFormSections";
import {
  getEmployeeDetails,
  getMyResignations,
  getResignationDetails,
  submitWithdrawal,
  formatDate,
  EmployeeDetails,
  WithdrawalRequest,
  SeparationListItem,
} from "./services/eSeparation.service";

const WithdrawalResignation: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const routes = all_routes;

  // Resolved employee master details (nice designation / department text).
  const [emp, setEmp] = useState<EmployeeDetails | null>(null);
  // Full raw resignation record straight from the find-all API.
  const [rec, setRec] = useState<any | null>(null);
  const [eligible, setEligible] = useState(true);
  const [eligibilityMessage, setEligibilityMessage] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { confirm, confirmDialog } = useConfirm();

  const today = new Date().toISOString().split("T")[0];
  const origRequestId =
    rec?.request_id || (location.state as any)?.orig_request_id || "";

  // Explain to the user exactly why the Submit button is disabled.
  const disabledReason = !eligible
    ? eligibilityMessage ||
      "You are not eligible for withdrawal. Your resignation may have already been approved by the Competent Authority."
    : !reason.trim()
    ? "Please provide a reason for withdrawal to enable submission."
    : reason.trim().length < 20
    ? "Reason must be at least 20 characters to enable submission."
    : "";

  useEffect(() => {
    getEmployeeDetails().then((res) => res.data && setEmp(res.data));
    getMyResignations().then((res) => {
      const list = res.data || [];

      // Only one withdrawal may be open per resignation. If a non-rejected
      // withdrawal already exists, block re-submission.
      const existingWithdrawal = list.find(
        (r) =>
          String(r.type ?? r.raw?.request_type ?? "")
            .toUpperCase()
            .includes("WITHDRAW") &&
          String(r.status ?? "").toUpperCase() !== "REJECTED"
      );
      if (existingWithdrawal) {
        setEligible(false);
        setEligibilityMessage(
          "A withdrawal request has already been submitted for your resignation. You cannot submit another one."
        );
      }

      // A withdrawal must target the parent RESIGNATION — never a child request
      // (short-notice / no-dues / a prior withdrawal).
      const isResignation = (r: SeparationListItem) =>
        String(r.type ?? r.raw?.request_type ?? "")
          .toUpperCase()
          .includes("RESIGN") || String(r.id ?? "").startsWith("RES-");
      const active = list.find(
        (r) =>
          isResignation(r) && r.status !== "Closed" && r.status !== "REJECTED"
      );
      const reqId = active?.raw?.request_id || active?.id;
      if (!reqId) return;

      // Seed with the find-all record immediately, then enrich with the full
      // find-one detail (designation_text, hierarchy, formatted dates, etc.).
      if (active?.raw) setRec(active.raw);
      getResignationDetails(reqId).then((res) => {
        if (res.data) setRec(res.data);
      });
    });
  }, []);

  const handleCancel = async () => {
    if (
      await confirm("Discard changes?", {
        title: "Cancel",
        confirmLabel: "Discard",
        tone: "danger",
      })
    )
      navigate(-1);
  };

  const handleSubmit = async () => {
    if (!eligible)
      return toast.warning(
        eligibilityMessage ||
          "A withdrawal request has already been submitted for your resignation."
      );
    if (!origRequestId)
      return toast.warning("No active resignation found to withdraw.");
    if (!reason.trim())
      return toast.warning("Please provide a reason for withdrawal.");
    if (reason.trim().length < 20)
      return toast.warning(
        "Please provide a detailed reason (minimum 20 characters)."
      );
    if (
      !(await confirm(
        "Are you sure you want to submit a withdrawal request? This will put your original resignation on hold.",
        { title: "Confirm Withdrawal", confirmLabel: "Submit", tone: "primary" }
      ))
    )
      return;
    const payload: WithdrawalRequest = {
      resignation_request_id: origRequestId,
      reason: reason.trim(),
    };
    setSubmitting(true);
    const res = await submitWithdrawal(payload);
    setSubmitting(false);
    if (res.success) {
      toast.success(
        "Your withdrawal request has been submitted successfully. Your original resignation is now on hold."
      );
      navigate(routes.Esepration.overview);
    } else {
      toast.error(res.message || "Failed to submit. Please try again.");
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content pb-0">
        <div
          className="card border-0 shadow-sm mb-4"
          style={{ borderRadius: 12 }}
        >
          <div className="card-body">
            <h3 className="page-title mb-1">
              Withdrawal of Intimation / Resignation
            </h3>
            <p className="text-muted mb-0">
              Cancel a previously submitted resignation request.
            </p>
          </div>
        </div>

        {eligible ? (
          <WarningBanner
            type="warning"
            message="Withdrawal is allowed during the notice period, at any stage of your resignation — even after the Competent Authority has approved it, until it is finally approved. Your resignation is put on hold at its current level, and your withdrawal is approved by the same approvers up to that level; the last of them gives the final approval."
          />
        ) : (
          <WarningBanner
            type="error"
            message={
              eligibilityMessage ||
              "You are not eligible for withdrawal. Your resignation may have already been approved by the Competent Authority."
            }
          />
        )}

        {/* Sections 1-2 — the original resignation (shared with the approval
            screens so both show exactly the same thing). */}
        <WithdrawalOriginalSections
          rec={rec}
          emp={emp}
          onViewComplete={() =>
            navigate(routes.Esepration.approvalDetail.replace(":id", rec.request_id), {
              state: { request_id: rec.request_id, type: "Intimation", role: "Employee", viewOnly: true },
            })
          }
        />

        {/* Section 3 — Withdrawal Request */}
        <SectionCard index={2} title="Withdrawal Request">
          <div className="row g-3">
            <div className="col-12">
              <FieldLabel label="Submission of Application for Withdrawal of Intimation of Resignation" />
              <ReadOnlyField value={formatDate(today)} />
              <small className="text-muted d-block mt-1">
                Auto-fetched on submission date.
              </small>
            </div>
            <div className="col-12">
              <FieldLabel label="Reason for Withdrawal" required />
              <textarea
                className="form-control"
                rows={5}
                placeholder="Please provide a detailed reason for withdrawing your resignation..."
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
              <small className="text-muted d-block mt-1">
                Minimum 20 characters required. Provide genuine reasons for
                withdrawal.
              </small>
            </div>
          </div>
        </SectionCard>
      </div>

      {confirmDialog}

      <BottomActionBar
        primaryLabel="Submit Withdrawal"
        primaryIcon="ti-arrow-back-up"
        secondaryLabel="Cancel"
        onPrimary={handleSubmit}
        onSecondary={handleCancel}
        primaryColor="#993C1D"
        disabled={!eligible || !reason.trim() || submitting}
        note={disabledReason}
      />
    </div>
  );
};

export default WithdrawalResignation;
